import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Typography,
  InputAdornment,
  Tooltip,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import TextField from "../../components/TextField";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import { useGrade } from "./hooks";
import Loader from "../../components/Loader";

export const category = {
  DIAGNOSTIC: "Ir Grading",
  COURSE: "Assessment",
};

export const Edit = () => {
  const { create, save, loadCurrent, current, loading } = useGrade();
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();

  const [gradingModel, setGradingModel] = useState({});
  const [gradeFields, setGradeFields] = useState([
    { gradeName: "", max: 0, min: 0 },
  ]);
  const [maxMark, setMaxMark] = useState("");
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  useEffect(() => {
    if (id) {
      loadCurrent({ id });
    }
  }, [id]);

  useEffect(() => {
    if (current) {
      setGradeFields(
        current?.details
          ? current.details
          : [{ gradeName: "", max: "", min: "" }],
      );
      setGradingModel(current);
      if (current?.details && current.details.length > 0) {
        setMaxMark(current.details[0].max); // Set maxMark from the first grade's max value
      }
    }
  }, [current]);

  useEffect(() => {
    setGradeFields((prevGradeFields) => {
      const newGradeFields = prevGradeFields.map((field, index) =>
        index === 0 ? { ...field, max: maxMark } : field,
      );
      return newGradeFields;
    });
  }, [maxMark]);

  useEffect(() => {
    const lastField = gradeFields[gradeFields.length - 1];
    if (lastField.min === "0") {
      setIsSubmitDisabled(false);
    } else {
      setIsSubmitDisabled(true);
    }
  }, [gradeFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setGradingModel((prevGrading) => ({
      ...prevGrading,
      [name]: value,
    }));
  };

  const handleInputChange = (index, e) => {
    const { name, value } = e.target;
    setGradeFields((prevGradeFields) => {
      const newGradeFields = [...prevGradeFields];
      const previousField = index > 0 ? newGradeFields[index - 1] : null;
      const currentField = { ...newGradeFields[index], [name]: value };

      if (name === "min") {
        const numValue = parseFloat(value);

        if (previousField && numValue >= parseFloat(previousField.max)) {
          toast.error(
            "Minimum mark must be less than the previous grade's maximum mark.",
          );
          return prevGradeFields;
        }

        if (numValue >= parseFloat(currentField.max)) {
          toast.error(
            "Minimum mark must be less than the current grade's maximum mark.",
          );
          currentField[name] = "";
          return newGradeFields;
        }

        if (numValue < 0) {
          toast.error("Minimum mark cannot be negative.");
          currentField[name] = "";
          return newGradeFields;
        }
      }

      newGradeFields[index] = currentField;
      return newGradeFields;
    });
  };

  const handleAddFields = () => {
    const lastField = gradeFields[gradeFields.length - 1];
    if (!lastField.gradeName || !lastField.max || lastField.min === "") {
      toast.error("Please fill in all fields before adding a new grade.");
      return;
    }

    const gradeNames = gradeFields.map((field) =>
      field.gradeName.toLowerCase(),
    );
    const hasDuplicates = gradeNames.some(
      (name, idx) => gradeNames.indexOf(name) !== idx,
    );
    if (hasDuplicates) {
      toast.error("Grading name must be unique.");
      return;
    }

    setGradeFields((prevGradeFields) => {
      const newGradeFields = [...prevGradeFields];
      const lastField = newGradeFields[newGradeFields.length - 1];
      const newField = { gradeName: "", max: "", min: "" };

      if (lastField && lastField.min !== "") {
        const lastMin = parseFloat(lastField.min);

        // Check if it's a decimal or integer
        if (lastMin % 1 === 0) {
          // It's an integer, subtract 1
          newField.max = Math.max(0, lastMin - 1).toString();
        } else {
          // It's a decimal, subtract 0.1
          newField.max = Math.max(0, lastMin - 0.1).toFixed(1);
        }
      }

      newGradeFields.push(newField);
      return newGradeFields;
    });
  };

  const handleRemoveFields = (index) => {
    setGradeFields((prevGradeFields) => {
      const newGradeFields = [...prevGradeFields];
      newGradeFields.splice(index, 1);
      return newGradeFields;
    });
  };

  const handleMaxMarkChange = (e) => {
    if (e.target.value >= 0) {
      setMaxMark(e.target.value);
    }

    // setMaxMark(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let temp = {
      ...gradingModel,
      details: gradeFields,
    };

    if (id) {
      temp = { ...temp, id };
      save({ grade: temp, navigate });
    } else {
      create({ grade: temp, navigate });
    }
  };

  return (
    // <>
    //  {loading ? <Loader /> :
    <Box
      sx={{
        padding: "0px 20px",
        [theme.breakpoints.down("md")]: {
          padding: "0px",
        },
      }}
    >
      <div className="question header-container">
        <div className="header">
          <Typography
            align="left"
            variant="h1"
            color="primary.blue"
            sx={{
              margin: "0",
              fontWeight: "700",
              lineHeight: "1.5",
              fontSize: "2rem",
            }}
          >
            {id ? `Edit Grading Model` : "Create Grading Model"}
          </Typography>
        </div>
        <form onSubmit={handleSubmit}>
          <Grid
            sx={styles.gridContainer}
            container
            spacing={2}
            alignItems="center"
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InputLabel style={styles.inputLabel}>
                Learning Category <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <FormControl fullWidth>
                <Select
                  name="category"
                  value={gradingModel?.category || ""}
                  onChange={(e) => handleChange(e)}
                  sx={{ height: "47px" }}
                >
                  {Object.entries(category).map(([apiValue, uiLabel]) => (
                    <MenuItem key={apiValue} value={apiValue}>
                      {uiLabel}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InputLabel style={styles.inputLabel}>
                Model Name <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <TextField
                name="gradingModelName"
                fullWidth
                value={gradingModel?.gradingModelName || ""}
                onChange={(e) => handleChange(e)}
                placeholder="Enter Grading Model Name"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InputLabel style={styles.inputLabel}>
                Maximum Mark for Exam <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <TextField
                name="maxMark"
                fullWidth
                value={maxMark}
                onChange={handleMaxMarkChange}
                placeholder="Enter Maximum Mark for Exam"
                required
                disabled={
                  gradeFields.length > 1 ||
                  gradeFields[0]?.min > 0 ||
                  !gradingModel?.gradingModelName
                }
                type="Number"
              />
            </Grid>
          </Grid>

          {gradeFields?.map((field, index) => (
            <Grid
              sx={styles.gridContainer}
              container
              spacing={2}
              alignItems="center"
              key={index}
            >
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>
                  Grade Name <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <TextField
                  name="gradeName"
                  fullWidth
                  value={field.gradeName}
                  onChange={(e) => handleInputChange(index, e)}
                  placeholder="Enter Grade Name"
                  required
                  disabled={index < gradeFields.length - 1}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>
                  Maximum Marks <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <TextField
                  name="max"
                  fullWidth
                  value={field.max}
                  onChange={(e) => handleInputChange(index, e)}
                  placeholder="Enter Maximum Marks"
                  required
                  disabled
                  type="Number"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>
                  Minimum Marks <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <TextField
                  name="min"
                  fullWidth
                  value={field.min}
                  onChange={(e) => handleInputChange(index, e)}
                  placeholder="Enter Minimum Marks"
                  required
                  disabled={index < gradeFields.length - 1}
                  type="Number"
                />
              </Grid>
              <Grid item xs={8} sm={4} md={4} lg={2}>
                <InputLabel style={styles.inputLabel}>
                  Activity Score
                </InputLabel>
                <TextField
                  name="activityScore"
                  fullWidth
                  value={field.activityScore}
                  onChange={(e) => handleInputChange(index, e)}
                  placeholder="Enter Activity Score "
                  required
                  type="Number"
                />
              </Grid>
              <Grid item xs={4} sm={2} md={4} lg={1}>
                {gradeFields.length > 1 && (
                  <IconButton
                    disabled={index < gradeFields.length - 1}
                    onClick={() => handleRemoveFields(index)}
                  >
                    <DeleteIcon
                      sx={{
                        color:
                          index < gradeFields.length - 1
                            ? ""
                            : "var(--mui-palette-error-main)",
                        fontSize: "1.2rem",
                      }}
                    />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={4} lg={3} mt={2}>
            <Button
              onClick={() => handleAddFields()}
              size="large"
              variant="contained"
              color="primary"
              placeholder={"Add Grade"}
              disabled={!isSubmitDisabled}
            />
          </Grid>
          {!isSubmitDisabled && (
            <Grid container>
              <Grid mt={2} item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>
                  PassGrade <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <FormControl fullWidth>
                  <Select
                    name="passGrade"
                    value={gradingModel?.passGrade || ""}
                    onChange={(e) => handleChange(e)}
                    sx={{ height: "47px" }}
                  >
                    {gradeFields
                      ?.filter((grade) => grade.gradeName)
                      .map((grade) => (
                        <MenuItem key={grade.gradeName} value={grade.gradeName}>
                          {grade.gradeName}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
          <Box sx={{ mt: 4 }}>
            <Button
              size="large"
              type="submit"
              variant="contained"
              color="primary"
              placeholder={!id ? "Create" : "Save Changes"}
              disabled={isSubmitDisabled}
            />
          </Box>
        </form>
      </div>
    </Box>
    // }</>
  );
};

const styles = {
  label: {
    color: "var(--mui-palette-common-black)",
    fontWeight: "bold",
  },
  inputLabel: {
    color: "var(--mui-palette-primary-darkGrey)",
    marginBottom: "5px",
    fontWeight: "600",
    fontSize: "14px",
  },
  gridContainer: {
    mt: 2,
    display: "flex",
  },
  suggestionBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "10px",
  },
  suggestionItem: {
    padding: "10px",
    border: "1px solid #ccc",
    borderRadius: "2px",
    cursor: "pointer",
    boxShadow: "0 4px 8px var(--mui-palette-secondary-boxShadow1)",
    backgroundColor: "var(--mui-palette-gray-lightGrey300)",
  },
  uploadBtn: {
    width: "50px",
    height: "50px",
    borderRadius: "5px",
  },
  hiddenInput: {
    display: "none",
  },
};
